var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-form.ts
import { Change, nothingHere } from "@adornis/base/change";
import { AdornisEntity, constructValue, upgradeMark } from "@adornis/baseql/entities/adornisEntity";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element";
import { BehaviorController } from "@adornis/chemistry/controllers/behavior-controller";
import { ElementsButton } from "@adornis/chemistry/elements/components/x-button";
import { ValidationError } from "@adornis/validation/errors/ValidationError";
import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { BehaviorSubject, firstValueFrom, fromEvent, NEVER, timer } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { EntityValidator } from "./entity-validator";
fromEvent(window, "beforeunload").subscribe((e) => {
  if (AdornisForm.warnBeforeUnload) {
    e.returnValue = true;
    return "There are unsaved changes. Do you really want to leave the page?";
  }
});
var assertIsFormfield = (el) => {
  if (!("value" in el) || // check if input.value exists
  !("disabled" in el)) {
    console.warn(
      "not a formfield (applyDoc): ",
      el,
      "value" in el,
      el.value,
      el.disabled
    );
    throw new Error("not a formfield");
  }
};
var AdornisForm = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this._document = null;
    this["enter-to-confirm"] = new BehaviorController(this, false);
    this["no-scroll"] = new BehaviorController(this, false);
    this.errorMessage = new BehaviorSubject(null);
    this._scrolledToWrongInput = false;
    this.slottedElementsObserver = new MutationObserver(() => this.reWatch());
    this.subscriptions = [];
  }
  set document(doc) {
    if (!doc)
      return;
    if (!doc[upgradeMark])
      throw new Error("AdornisForm only accepts instantiated AdornisEntities");
    if (!this.validator && doc)
      this.validator = EntityValidator.fromEntity(doc.constructor);
    this.applyDoc(!doc || doc[upgradeMark] ? doc : this.validator.construct(doc));
  }
  get document() {
    return this._document;
  }
  connectedCallback() {
    super.connectedCallback();
    fromEvent(this, "click").pipe(takeUntil(this.disconnected)).subscribe((e) => {
      e.composedPath().some((node) => {
        if (node.getAttribute && node.getAttribute("validate") === "") {
          e.stopPropagation();
          this.validate();
          return true;
        }
        return false;
      });
    });
    timer(50).pipe(takeUntil(this.disconnected)).subscribe(() => {
      this.getAllInputs().forEach((input) => this.registerEnterHandler(input));
    });
  }
  slottedElementsChanged() {
    this.slottedElementsObserver.disconnect();
    const slottedNodes = this._slot.assignedNodes();
    slottedNodes.forEach((node) => this.slottedElementsObserver.observe(node, { childList: true, subtree: true }));
    this.reWatch();
  }
  render() {
    return html`
      <slot
        id="slot"
        ${ref((slot) => this._slot = slot)}
        @slotchange=${() => this.slottedElementsChanged()}
      ></slot>
    `;
  }
  get ready() {
    return firstValueFrom(this._connected.pipe(filter(Boolean)));
  }
  async applyDoc(doc) {
    if (!this.validator)
      return console.error("no validator");
    if (!doc)
      return console.error("no doc");
    this._document = doc;
    await this.ready;
    if (doc._class !== this.validator.entityClass._class)
      doc._class = this.validator.entityClass._class;
    this.getAllInputs().forEach((input) => {
      const fieldName = input.getAttribute("a-form-key");
      try {
        assertIsFormfield(input);
        const value = Change.resolvePath(doc || {}, fieldName.split("."));
        if (value !== nothingHere)
          if (input.value.next)
            input.value.next(value);
          else
            input.value = value;
      } catch (e) {
        console.warn("error applying value: ", e);
      }
    });
  }
  reWatch() {
    this.stopFormFieldWatcher();
    this.applyDoc(this.document);
    this.restartFormfieldWatcher();
  }
  stopFormFieldWatcher() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  async restartFormfieldWatcher() {
    await this.ready;
    this.subscriptions = this.getAllInputs().map((input) => {
      assertIsFormfield(input);
      return [
        fromEvent(input, "value-changed").subscribe((e) => {
          this.updateErrorMessage(e.detail.value, input);
        }),
        fromEvent(input, "value-picked").subscribe((e) => {
          this.applySingleChange(e.detail.value, input);
        })
      ];
    }).flat();
  }
  fillDocumentAndReturn() {
    if (!this.document) {
      console.warn("wanting to fill null document");
      return null;
    }
    this.getAllInputs().forEach((keyElement) => {
      if (keyElement)
        this.applySingleChange(keyElement.value.getValue(), keyElement);
    });
    return this.document;
  }
  applySingleChange(value, keyElement) {
    try {
      const pathParts = keyElement.getAttribute("a-form-key").split(".");
      const resolvedValue = Change.resolvePath(this.document, pathParts);
      if (resolvedValue === nothingHere) {
        let current = this.document || {};
        pathParts.forEach((pathPart, i) => {
          if (i !== pathParts.length - 1 && (!(pathPart in current) || current[pathPart] === void 0))
            current[pathPart] = {};
          current = current[pathPart];
        });
      }
      new Change(value, pathParts).applyToContext(this.document, true);
      this.getAllInputsByKey(keyElement.getAttribute("a-form-key")).forEach((element) => {
        if ((element.value.getValue?.() || element.value) !== value) {
          if (element.value.next)
            element.value.next(value);
          else
            element.value = value;
          element.error = void 0;
        }
      });
      keyElement.error = void 0;
      this.validator.validateField(this.document || {}, pathParts);
    } catch (e) {
      if (e instanceof ValidationError)
        this.showErrorForField(e, constructValue(this.document));
      else
        console.error("Error while applying single change: ", e);
    }
  }
  updateErrorMessage(value, keyElement) {
    try {
      const pathParts = keyElement.getAttribute("a-form-key").split(".");
      this.validator.validateFieldValue(value, this.document || {}, pathParts);
      keyElement.error = void 0;
    } catch {
    }
  }
  async validate() {
    if (!this.validator)
      throw new Error("validator is missing");
    const buttons = Array.from(this.querySelectorAll("[validate]"));
    buttons.forEach((button) => {
      if (button instanceof ElementsButton)
        button.loading = true;
    });
    this.dispatchEvent(new CustomEvent("validating"));
    let rawDoc = this.fillDocumentAndReturn();
    let currDoc = rawDoc?.[upgradeMark] ? rawDoc : constructValue(rawDoc);
    let entity = null;
    this.clearAllErrors();
    try {
      await Promise.all(
        this.getAllInputs().map((input) => {
          input.error = void 0;
          return input.preValidate?.();
        })
      );
      rawDoc = this.fillDocumentAndReturn();
      currDoc = rawDoc?.[upgradeMark] ? rawDoc : constructValue(rawDoc);
      if (!currDoc)
        throw new Error("cant validate non-existent document");
      if (this.errorMessage.getValue())
        throw this.errorMessage.getValue();
      entity = await this.validator.validate(currDoc);
      await Promise.all(this.getAllInputs().map((input) => input.postValidate?.()));
      await this.onSuccess?.(entity);
      this.dispatchEvent(new CustomEvent("success", { detail: entity, bubbles: false, composed: false }));
    } catch (e) {
      this._getAllInputsByValidationError(e, currDoc).forEach(
        (input) => input.error = this.translateErrors ? this.translateErrors(e.extensions?.translationKey || e.msg || e.message) : e.msg || e.message
      );
      await Promise.all(this.getAllInputs().map((input) => input.postValidate?.(true)));
      this.showError(e, currDoc);
    }
    buttons.forEach((button) => {
      if (button instanceof ElementsButton)
        button.loading = false;
    });
    return entity;
  }
  showError(e, constructedDocument) {
    const wrongInput = this._getAllInputsByValidationError(e, constructedDocument)?.[0];
    if (wrongInput) {
      if (!this._scrolledToWrongInput && !this["no-scroll"].value) {
        wrongInput.scrollIntoView();
        this._scrolledToWrongInput = true;
      }
      wrongInput.error = this.translateErrors ? this.translateErrors(e.extensions?.translationKey || e.message) : e.message;
    }
    this.errorMessage.next(e);
    this.dispatchEvent(new CustomEvent("error", { detail: e }));
  }
  showErrorForField(e, constructedDocument) {
    const wrongInput = this._getAllInputsByValidationError(e, constructedDocument)?.[0];
    if (wrongInput) {
      if (!this._scrolledToWrongInput && !this["no-scroll"].value) {
        wrongInput.scrollIntoView();
        this._scrolledToWrongInput = true;
      }
      wrongInput.error = this.translateErrors ? this.translateErrors(e.extensions?.translationKey || e.message) : e.message;
    }
    this.errorMessage.next(e);
    this.dispatchEvent(new CustomEvent("field-error", { detail: e }));
  }
  clearAllErrors() {
    this._scrolledToWrongInput = false;
    this.errorMessage.next(null);
    this.getAllInputs().forEach((inp) => inp.error = void 0);
  }
  getAllInputsByKey(key) {
    return Array.from(this.renderRoot.querySelectorAll(`[a-form-key="${key}"]`)).concat(Array.from(this.querySelectorAll(`[a-form-key="${key}"]`)));
  }
  // private getInputByKey(key: string) {
  //   return this.getAllInputsByKey(key)[0];
  // }
  getAllInputs() {
    return Array.from(this.renderRoot.querySelectorAll("[a-form-key]")).concat(
      Array.from(this.querySelectorAll("[a-form-key]"))
    );
  }
  registerEnterHandler(el) {
    if (!el.getAttribute("a-form-key"))
      return;
    this["enter-to-confirm"].pipe(
      switchMap((bool) => bool ? fromEvent(el, "keypress") : NEVER),
      takeUntil(this.disconnected)
    ).subscribe((e) => {
      if (e.code === "Enter")
        this.validate();
    });
  }
  _getAllInputsByValidationError(e, constructedDocument) {
    const pathToEntity = getPathToEntityFromValidationError(e, this.validator.entityClass, constructedDocument);
    return this.getAllInputsByKey(
      e.extensions ? concatEntityPath(pathToEntity, e.extensions.key) : pathToEntity
    );
  }
};
AdornisForm.warnBeforeUnload = false;
__decorateClass([
  property()
], AdornisForm.prototype, "translateErrors", 2);
__decorateClass([
  property({ type: Boolean })
], AdornisForm.prototype, "enter-to-confirm", 2);
__decorateClass([
  property({ type: Boolean })
], AdornisForm.prototype, "no-scroll", 2);
AdornisForm = __decorateClass([
  customElement("x-form")
], AdornisForm);
var getPathToEntityFromValidationError = (error, entityClass, entity, currentSelector = "", visited = /* @__PURE__ */ new Set()) => {
  if (visited.has(entity) || error.instance === entity)
    return "";
  visited.add(entity);
  for (const field of entityClass.fields) {
    if (entity.has(field.name) && entity[field.name] === error.instance)
      return `${currentSelector}${currentSelector.length ? "." : ""}${field.name}`;
    if (entity.has(field.name) && entity[field.name] instanceof AdornisEntity) {
      const selectorExtension = getPathToEntityFromValidationError(
        error,
        entity[field.name].constructor,
        entity[field.name],
        field.name,
        visited
      );
      if (selectorExtension && Change.resolvePath(entity, selectorExtension.split(".")) === error.instance)
        return `${currentSelector}${currentSelector.length ? "." : ""}${selectorExtension}`;
    }
    if (
      // @ts-ignore ignore entity.has type error
      entity.has(field.name) && Array.isArray(entity[field.name]) && entity[field.name].some((x) => x instanceof AdornisEntity)
    ) {
      for (let i = 0; i < entity[field.name].length; i++) {
        const selectorExtension = getPathToEntityFromValidationError(
          error,
          entity[field.name][i].constructor,
          entity[field.name][i],
          "",
          visited
        );
        if (Change.resolvePath(entity, [field.name, i, ...selectorExtension ? selectorExtension.split(".") : []]) === error.instance)
          return `${currentSelector}${currentSelector.length ? "." : ""}${field.name}.${i}${selectorExtension ? "." : ""}${selectorExtension}`;
      }
    }
  }
  return currentSelector;
};
var concatEntityPath = (...entityPaths) => entityPaths.reduce((acc, curr) => `${acc}${acc.length ? "." : ""}${curr}`, "");
export {
  AdornisForm
};
